import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import type { EditorContents } from "./editorContents";
import {
  freshUntitledScriptId,
  isSchemaEditorId,
  isUntitledScriptId,
  type StageTabRole,
  schemaEditorId,
  schemaIdOfInstance,
  TRANSFORM_EDITOR_ID,
  unattachedSchemaEditorId,
} from "./stagingModel";

// Unsaved editor drafts, one per nav-bar row: SCHEMA slots and staged
// schema instances, transform rows and the TRANSFORM pseudo-row. Typing
// writes through to the selected row's draft, so leaving a row never loses
// the edits.

type DraftMap = Record<string, string>;
type SetDraftMap = Dispatch<SetStateAction<DraftMap>>;

// The three draft-map setters bundled together, so helpers that touch more
// than one map take a single argument instead of a setter per map.
type DraftMapSetters = {
  setSchemaDrafts: SetDraftMap;
  setDraftOrigins: SetDraftMap;
  setSchemaDraftNames: SetDraftMap;
};

// Drops one key; the identical reference when the key is absent lets React
// state setters skip the update (same for the other helpers below).
const withoutKey = (prev: DraftMap, key: string): DraftMap => {
  if (!(key in prev)) return prev;
  const { [key]: _dropped, ...rest } = prev;
  return rest;
};

// Drops every key matching the predicate.
const withoutMatching = (
  prev: DraftMap,
  drop: (key: string) => boolean,
): DraftMap => {
  if (!Object.keys(prev).some(drop)) return prev;
  return Object.fromEntries(Object.entries(prev).filter(([key]) => !drop(key)));
};

// Moves one key's entry under a new key.
const withKeyMoved = (
  prev: DraftMap,
  oldKey: string,
  newKey: string,
): DraftMap => {
  if (!(oldKey in prev)) return prev;
  const { [oldKey]: moved, ...rest } = prev;
  return { ...rest, [newKey]: moved ?? "" };
};

// Renames every key matching the predicate with the given mapping.
const withKeysRenamed = (
  prev: DraftMap,
  affected: (key: string) => boolean,
  renamed: (key: string) => string,
): DraftMap => {
  if (!Object.keys(prev).some(affected)) return prev;
  return Object.fromEntries(
    Object.entries(prev).map(([key, value]) =>
      affected(key) ? [renamed(key), value] : [key, value],
    ),
  );
};

// A stage rename changes its SCHEMA slot's block key — the slot's own
// draft and any instance drafts keyed under the block follow the new id.
function renameBlockDrafts(
  oldBlockKey: string,
  newBlockKey: string,
  setters: DraftMapSetters,
) {
  const suffix = `|${oldBlockKey}`;
  const affected = (key: string) => key === oldBlockKey || key.endsWith(suffix);
  const renamed = (key: string) =>
    `${key.slice(0, key.length - oldBlockKey.length)}${newBlockKey}`;
  setters.setSchemaDrafts((prev) => withKeysRenamed(prev, affected, renamed));
  setters.setDraftOrigins((prev) => withKeysRenamed(prev, affected, renamed));
  setters.setSchemaDraftNames((prev) =>
    withKeysRenamed(prev, affected, renamed),
  );
}

// Moves a tab's bottom-block draft (and its name) into a newly staged
// stage's SCHEMA slot; false when there is no draft to move.
function moveDraftIntoStage(
  role: StageTabRole,
  stageId: string,
  schemaDrafts: DraftMap,
  setters: Pick<DraftMapSetters, "setSchemaDrafts" | "setSchemaDraftNames">,
): boolean {
  const slotRowId = unattachedSchemaEditorId(role);
  if (!schemaDrafts[slotRowId]) return false;
  const targetRowId = schemaEditorId(stageId);
  setters.setSchemaDrafts((prev) => withKeyMoved(prev, slotRowId, targetRowId));
  setters.setSchemaDraftNames((prev) =>
    withKeyMoved(prev, slotRowId, targetRowId),
  );
  return true;
}

// Remembers the stored text an instance draft diverged from — restored
// with the draft so the dirty asterisk survives the trip.
function rememberOrigin(
  setOrigins: SetDraftMap,
  rowId: string,
  origin: string | null,
) {
  if (origin === null) return;
  setOrigins((prev) =>
    prev[rowId] === origin ? prev : { ...prev, [rowId]: origin },
  );
}

// What a schema keystroke does to the selected row's remembered draft:
// SCHEMA editor rows keep every keystroke (the untitled-row flow keys on
// draft !== ""); staged-row instances keep a draft only while the text
// diverges from the loaded definition — back at the original means no
// draft, no dirty asterisk. Other rows keep no schema drafts at all.
function schemaDraftAction(
  rowId: string,
  value: string,
  origin: string | null,
): "keep" | "diverged" | "clear" | "none" {
  if (isSchemaEditorId(rowId)) return "keep";
  if (schemaIdOfInstance(rowId) === null) return "none";
  return value === origin ? "clear" : "diverged";
}

// True for the keystroke that STARTS a slot draft from empty — the mint
// moment for its untitled row (the untitled row shows only once the draft
// is non-empty, see hasDraft), as opposed to a later keystroke landing in
// an already-drafted slot.
function startsFreshDraft(value: string, existingDraft: string | undefined) {
  return value.trim() !== "" && (existingDraft ?? "").trim() === "";
}

// One schema-editor keystroke as the draft maps see it: which row it
// landed in, the new text, what was drafted before, and the stored text
// the draft diverged from.
type SchemaKeystroke = {
  rowId: string;
  value: string;
  existingDraft: string | undefined;
  origin: string | null;
};

// Writes a "keep"/"diverged" keystroke into the draft maps: flags the
// slot's fresh-mint row when the draft is just starting, records the
// keystroke, and remembers the origin it diverged from.
function applyDraftKeystroke(
  action: "keep" | "diverged",
  keystroke: SchemaKeystroke,
  setters: Pick<DraftMapSetters, "setSchemaDrafts" | "setDraftOrigins"> & {
    setFreshSchemaDraftRowId: Dispatch<SetStateAction<string | null>>;
  },
) {
  const { rowId, value, existingDraft, origin } = keystroke;
  if (action === "keep" && startsFreshDraft(value, existingDraft)) {
    setters.setFreshSchemaDraftRowId(rowId);
  }
  setters.setSchemaDrafts((prev) => ({ ...prev, [rowId]: value }));
  if (action === "diverged") {
    rememberOrigin(setters.setDraftOrigins, rowId, origin);
  }
}

// One schema keystroke routed by what it means for the selected row's
// draft (schemaDraftAction): cleared back to the original, or recorded.
function routeSchemaKeystroke(
  keystroke: SchemaKeystroke,
  handlers: { clearDraftFor: (rowId: string) => void } & Pick<
    DraftMapSetters,
    "setSchemaDrafts" | "setDraftOrigins"
  > & { setFreshSchemaDraftRowId: Dispatch<SetStateAction<string | null>> },
) {
  const action = schemaDraftAction(
    keystroke.rowId,
    keystroke.value,
    keystroke.origin,
  );
  if (action === "none") return;
  if (action === "clear") {
    handlers.clearDraftFor(keystroke.rowId);
    return;
  }
  applyDraftKeystroke(action, keystroke, handlers);
}

// The schema editor's onChange: writes through to the editor document and
// routes the keystroke into the selected row's draft.
function useSchemaKeystrokes(
  editor: EditorContents,
  effectiveSelectedId: string | null,
  schemaDrafts: DraftMap,
  handlers: { clearDraftFor: (rowId: string) => void } & Pick<
    DraftMapSetters,
    "setSchemaDrafts" | "setDraftOrigins"
  > & { setFreshSchemaDraftRowId: Dispatch<SetStateAction<string | null>> },
) {
  const live = { editor, effectiveSelectedId, schemaDrafts, handlers };
  const liveRef = useRef(live);
  liveRef.current = live;
  return useCallback((value: string) => {
    const current = liveRef.current;
    current.editor.setSchemaDocument(value);
    const rowId = current.effectiveSelectedId;
    if (rowId === null) return;
    routeSchemaKeystroke(
      {
        rowId,
        value,
        existingDraft: current.schemaDrafts[rowId],
        origin: current.editor.schemaOrigin,
      },
      current.handlers,
    );
  }, []);
}

// Whether the seeded slot's editor row is the one on screen (block rows
// select as "<block>|<slot>" composites).
const seedOnScreen = (selectedId: string | null, rowId: string) =>
  selectedId === rowId || (selectedId?.endsWith(`|${rowId}`) ?? false);

// Seeds a slot draft programmatically — the dropped source's auto schema
// (TTTL-179 gap 4): content plus display name, overwriting a previous
// seed on variant switch. When the seeded row is the one on screen the
// schema editor follows along.
function useSeedDraft(
  editor: EditorContents,
  effectiveSelectedId: string | null,
  setters: Pick<DraftMapSetters, "setSchemaDrafts" | "setSchemaDraftNames">,
) {
  const { setSchemaDrafts, setSchemaDraftNames } = setters;
  return useCallback(
    (rowId: string, name: string, text: string) => {
      setSchemaDrafts((prev) => ({ ...prev, [rowId]: text }));
      setSchemaDraftNames((prev) => ({ ...prev, [rowId]: name }));
      if (seedOnScreen(effectiveSelectedId, rowId)) {
        editor.setSchemaDocument(text);
      }
    },
    [
      effectiveSelectedId,
      editor.setSchemaDocument,
      setSchemaDrafts,
      setSchemaDraftNames,
    ],
  );
}

// Unsaved schema drafts, one per SCHEMA slot (keyed by the slot's editor
// row id) — they stay staged in their block across selection changes and as
// more blocks arrive, until saved or discarded.
export function useSchemaDrafts(
  editor: EditorContents,
  effectiveSelectedId: string | null,
) {
  const [schemaDrafts, setSchemaDrafts] = useState<DraftMap>({});
  // The stored definition an instance draft diverged from — restored as
  // schemaOrigin with the draft so the dirty asterisk survives the trip.
  const [draftOrigins, setDraftOrigins] = useState<DraftMap>({});
  // Display names for the slot drafts' "Untitled schema*" rows — local
  // until the Save modal names the stored schema; missing = the default.
  const [schemaDraftNames, setSchemaDraftNames] = useState<DraftMap>({});
  // The just-minted slot draft whose row opens its name input hands-off
  // (the mint keystroke is landing in the schema editor); consumed by
  // the row on mount.
  const [freshSchemaDraftRowId, setFreshSchemaDraftRowId] = useState<
    string | null
  >(null);
  const clearFreshSchemaDraft = useCallback(
    () => setFreshSchemaDraftRowId(null),
    [],
  );
  const setSchemaDraftName = useCallback((rowId: string, name: string) => {
    setSchemaDraftNames((prev) => ({ ...prev, [rowId]: name }));
  }, []);

  const clearDraftFor = useCallback((rowId: string) => {
    setSchemaDrafts((prev) => withoutKey(prev, rowId));
    setDraftOrigins((prev) => withoutKey(prev, rowId));
    setSchemaDraftNames((prev) => withoutKey(prev, rowId));
  }, []);

  // Drops every instance draft a schema holds (its rows across all
  // blocks) — for when the schema leaves staging entirely.
  const clearDraftsForSchema = useCallback((schemaId: string) => {
    const ofSchema = (key: string) => key.startsWith(`${schemaId}|`);
    setSchemaDrafts((prev) => withoutMatching(prev, ofSchema));
    setDraftOrigins((prev) => withoutMatching(prev, ofSchema));
  }, []);

  const setSchemaDocument = useSchemaKeystrokes(
    editor,
    effectiveSelectedId,
    schemaDrafts,
    {
      clearDraftFor,
      setSchemaDrafts,
      setDraftOrigins,
      setFreshSchemaDraftRowId,
    },
  );

  // Discards a slot's draft (the untitled row's minus); the editor clears
  // when that slot's row is the one on screen.
  const discardSchemaDraft = useCallback(
    (rowId: string) => {
      clearDraftFor(rowId);
      if (effectiveSelectedId === rowId) editor.clearEditor("schema");
    },
    [clearDraftFor, effectiveSelectedId, editor.clearEditor],
  );

  const moveBlockDrafts = useCallback(
    (oldBlockKey: string, newBlockKey: string) =>
      renameBlockDrafts(oldBlockKey, newBlockKey, {
        setSchemaDrafts,
        setDraftOrigins,
        setSchemaDraftNames,
      }),
    [],
  );

  const moveDraftToStage = useCallback(
    (role: StageTabRole, stageId: string): boolean =>
      moveDraftIntoStage(role, stageId, schemaDrafts, {
        setSchemaDrafts,
        setSchemaDraftNames,
      }),
    [schemaDrafts],
  );

  const clearAllDrafts = useCallback(() => {
    setSchemaDrafts({});
    setSchemaDraftNames({});
  }, []);

  const seedDraft = useSeedDraft(editor, effectiveSelectedId, {
    setSchemaDrafts,
    setSchemaDraftNames,
  });

  return useMemo(
    () => ({
      schemaDrafts,
      draftOrigins,
      schemaDraftNames,
      setSchemaDraftName,
      freshSchemaDraftRowId,
      clearFreshSchemaDraft,
      clearDraftFor,
      clearDraftsForSchema,
      setSchemaDocument,
      discardSchemaDraft,
      moveBlockDrafts,
      moveDraftToStage,
      clearAllDrafts,
      seedDraft,
    }),
    [
      schemaDrafts,
      draftOrigins,
      schemaDraftNames,
      setSchemaDraftName,
      freshSchemaDraftRowId,
      clearFreshSchemaDraft,
      clearDraftFor,
      clearDraftsForSchema,
      setSchemaDocument,
      discardSchemaDraft,
      moveBlockDrafts,
      moveDraftToStage,
      clearAllDrafts,
      seedDraft,
    ],
  );
}

// The row a script keystroke belongs to: a staged transform row or an
// untitled draft row keeps its own draft; anything else (the pseudo-row's
// fresh cycle, the resting tab where selection still sits on the source
// default, a tree preview) MINTS a new untitled draft — null tells the
// caller to allocate one, so any number of Untitled script rows can be
// drafted side by side.
function scriptDraftRow(
  rowId: string | null,
  isTransformRow: (id: string) => boolean,
): string | null {
  if (rowId !== null && isUntitledScriptId(rowId)) return rowId;
  if (rowId !== null && isTransformRow(rowId)) return rowId;
  return null;
}

// Script edits mirror the schema drafts model, one instance per transform
// row (a script serves exactly one stage): typing writes through to the
// selected row's draft, kept only while the text diverges from the stored
// script. The TRANSFORM editor pseudo-row keeps every keystroke — its
// non-empty draft is the "Untitled script*" row.
export function useScriptDrafts(
  editor: EditorContents,
  effectiveSelectedId: string | null,
  isTransformRow: (id: string) => boolean,
  setSelectedId: (id: string | null) => void,
  setEditorSource: (pill: null) => void,
  /** Appends the minted draft's staged-row face to the transforms list. */
  addUntitledScriptRow: (id: string) => void,
) {
  const [scriptDrafts, setScriptDrafts] = useState<DraftMap>({});
  const [scriptDraftOrigins, setScriptDraftOrigins] = useState<DraftMap>({});
  // The stored text of a staged row as the Studio last saved it
  // (TTTL-224): a run or the AI Helper reads it here instead of fetching
  // back the file that was just written. Kept across a draft's clearing;
  // dropped when the row leaves staging.
  const [savedScripts, setSavedScripts] = useState<DraftMap>({});

  const rememberSavedScript = useCallback((rowId: string, text: string) => {
    setSavedScripts((prev) =>
      prev[rowId] === text ? prev : { ...prev, [rowId]: text },
    );
  }, []);

  const forgetSavedScript = useCallback((rowId: string) => {
    setSavedScripts((prev) => withoutKey(prev, rowId));
  }, []);

  const clearScriptDraft = useCallback((rowId: string) => {
    setScriptDrafts((prev) => withoutKey(prev, rowId));
    setScriptDraftOrigins((prev) => withoutKey(prev, rowId));
  }, []);

  const setScriptDocument = useCallback(
    (value: string) => {
      editor.setScriptDocument(value);
      const rowId = scriptDraftRow(effectiveSelectedId, isTransformRow);
      if (rowId === null) {
        // The keystroke/template that starts a fresh script mints its own
        // "Untitled script*" row (a real transforms-list entry, so it
        // keeps its place among dropped scripts) and hands it the
        // selection — leaving it on the source default would show the new
        // row deselected (and a tree pill is mutually exclusive with a
        // row selection).
        const id = freshUntitledScriptId();
        setScriptDrafts((prev) => ({ ...prev, [id]: value }));
        addUntitledScriptRow(id);
        setSelectedId(id);
        setEditorSource(null);
        return;
      }
      if (isUntitledScriptId(rowId)) {
        setScriptDrafts((prev) => ({ ...prev, [rowId]: value }));
        return;
      }
      if (value === editor.scriptOrigin) {
        clearScriptDraft(rowId);
        return;
      }
      setScriptDrafts((prev) => ({ ...prev, [rowId]: value }));
      rememberOrigin(setScriptDraftOrigins, rowId, editor.scriptOrigin);
    },
    [
      editor.setScriptDocument,
      editor.scriptOrigin,
      effectiveSelectedId,
      isTransformRow,
      clearScriptDraft,
      setSelectedId,
      setEditorSource,
      addUntitledScriptRow,
    ],
  );

  // An untitled row's minus: drops that row's draft; when it was the one
  // on the editor, the transform pseudo-row takes the hand-off (editor to
  // its zero state, selection to the pseudo-row).
  const discardScriptDraft = useCallback(
    (id: string) => {
      clearScriptDraft(id);
      if (effectiveSelectedId === id) {
        editor.clearEditor("script");
        setSelectedId(TRANSFORM_EDITOR_ID);
      }
    },
    [clearScriptDraft, effectiveSelectedId, editor.clearEditor, setSelectedId],
  );

  // A rename changes the row's id (the filename IS the id) — the draft and
  // its origin move with it.
  const moveScriptDraft = useCallback((oldId: string, newId: string) => {
    setScriptDrafts((prev) => withKeyMoved(prev, oldId, newId));
    setScriptDraftOrigins((prev) => withKeyMoved(prev, oldId, newId));
    setSavedScripts((prev) => withKeyMoved(prev, oldId, newId));
  }, []);

  const clearAllScriptDrafts = useCallback(() => {
    setScriptDrafts({});
    setScriptDraftOrigins({});
    setSavedScripts({});
  }, []);

  return useMemo(
    () => ({
      scriptDrafts,
      scriptDraftOrigins,
      savedScripts,
      rememberSavedScript,
      forgetSavedScript,
      clearScriptDraft,
      setScriptDocument,
      discardScriptDraft,
      moveScriptDraft,
      clearAllScriptDrafts,
    }),
    [
      scriptDrafts,
      scriptDraftOrigins,
      savedScripts,
      rememberSavedScript,
      forgetSavedScript,
      clearScriptDraft,
      setScriptDocument,
      discardScriptDraft,
      moveScriptDraft,
      clearAllScriptDrafts,
    ],
  );
}

export type SchemaDrafts = ReturnType<typeof useSchemaDrafts>;
export type ScriptDrafts = ReturnType<typeof useScriptDrafts>;
